import { useSyncExternalStore } from "react";
import { STRINGS } from "./strings";

export type Locale = "en" | "fr";

export const LOCALES: Locale[] = ["en", "fr"];

const STORAGE_KEY = "europea.locale";

const isLocale = (value: unknown): value is Locale =>
  value === "en" || value === "fr";

/**
 * A module-level store rather than a context provider. The onboarding steps are
 * registered in `onboardingPages` and render bare — there is no shared parent to
 * hang a provider on short of the app root, and the standing rule is that this
 * work must not reach into the rest of the site. `useSyncExternalStore` gives
 * every step the same locale with no wrapper.
 */
const read = (): Locale => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : "en";
  } catch {
    // Private mode / storage disabled: fall back rather than break the step.
    return "en";
  }
};

let current: Locale = read();
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => current;

export const setLocale = (next: Locale) => {
  if (next === current) return;
  current = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Non-fatal: the choice just will not survive a reload.
  }
  listeners.forEach((listener) => listener());
};

/**
 * `t` is the dictionary itself, not a lookup function, so a missing or
 * misspelled key is a compile error instead of a blank space in the UI.
 */
export const useLocale = () => {
  const locale = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { locale, setLocale, t: STRINGS[locale] };
};
